import { semanasDelMes } from "@/lib/gastoFijo";
import { weekBounds } from "@/lib/semanas";
import { semanasProyeccion, filasProyeccion, planSemana } from "@/lib/proyeccionMensual";

const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
const sk = (w) => `${w.year}-${w.week}`;
const GESTIONABLES = ["presupuesto_planta", "extraordinarios", "provisiones_planta", "comisiones", "descuentos", "hgn_imp"];
const FIJO = ["corporativos", "impuestos_federales", "costo_bancario", "deuda_bancaria", "inversiones"];
const sum0 = (xs) => xs.reduce((a, x) => a + (Number.isFinite(x) ? x : 0), 0);
const dia = (d) => `${d.getUTCDate()} ${MESES[d.getUTCMonth()].slice(0, 3)}`;

// Meses que contienen d\u00edas de alguna semana del a\u00f1o (incluye dic. anterior / ene. siguiente).
export function mesesDelAnio(year) {
  const cands = [{ year: year - 1, month: 12 }, ...Array.from({ length: 12 }, (_, i) => ({ year, month: i + 1 })), { year: year + 1, month: 1 }];
  return cands.filter((m) => semanasDelMes(m.year, m.month).some((w) => w.year === year));
}

// Por mes: filas (parte de cada semana dentro del mes), renglones reales/proyecci\u00f3n y plan (METAS).
function porMes(m, d) {
  const { filas, ventaMes } = semanasProyeccion(m, d.vars, d.precios);
  const ctx = { ventaMes, ym: m, gp: d.gp, gf: d.gf, vars: d.vars };
  const rows = filasProyeccion({ filas, ...ctx });
  const byK = (rs) => Object.fromEntries(rs.filter((r) => !r.gap).map((r) => [r.k, r]));
  return {
    rows,
    segs: filas.map((f) => {
      const { fila: planFila, planVars } = planSemana(f, d.vars, m);
      return { ym: m, fila: f, rows: byK(rows), planFila, planRows: byK(filasProyeccion({ ...ctx, filas: [planFila], vars: planVars })) };
    }),
  };
}

export const valorSeg = (s, k, plan) => (plan ? s.planRows[k]?.w(s.planFila) : s.rows[k]?.w(s.fila)) ?? null;

export function columna(segs, extra = {}) {
  return {
    segs, dias: segs.reduce((a, s) => a + s.fila.dias, 0), real: segs.every((s) => s.fila.real),
    kg: sum0(segs.map((s) => s.fila.kg)), planKg: sum0(segs.map((s) => s.planFila.kg)), ...extra,
  };
}

// Semanas completas (domingo a s\u00e1bado) del a\u00f1o: una columna por semana sumando las complementarias de cada mes.
export function semanasAnio(year, meses, datos) {
  const map = new Map();
  let plantilla = null;
  meses.forEach((m, i) => {
    const { rows, segs } = porMes(m, datos[i]);
    if (!plantilla) plantilla = rows;
    segs.filter((s) => s.fila.year === year).forEach((s) => {
      const key = sk(s.fila);
      if (!map.has(key)) map.set(key, { year: s.fila.year, week: s.fila.week, segs: [] });
      map.get(key).segs.push(s);
    });
  });
  const semanas = [...map.values()].sort((a, b) => a.week - b.week).map((w) => {
    const [ini, fin] = weekBounds(w.year, w.week);
    return columna(w.segs, { year: w.year, week: w.week, sub: `${dia(ini)} al ${dia(fin)}` });
  });
  const base = (plantilla || []).map((r) => (r.gap ? r : { ...r, w: (c) => sum0(c.segs.map((s) => valorSeg(s, r.k, c.plan))) }));
  const by = Object.fromEntries(base.filter((r) => !r.gap).map((r) => [r.k, r]));
  const tot = (ks) => (c) => sum0(ks.map((k) => by[k]?.w(c)));
  const b2 = tot(GESTIONABLES), b3 = tot(FIJO);
  const mg = (c) => sum0([by.pv?.w(c), -(by.pc?.w(c) ?? 0), -(by.flete?.w(c) ?? 0)]);
  const calc = { mg, b2, b3, resultado: (c) => mg(c) - b2(c) - b3(c) };
  const rows = base.map((r) => (calc[r.k] ? { ...r, w: calc[r.k] } : r));
  return { semanas, rows };
}

// Columnas del modal: Complementaria A/B (si la semana cruza meses) y Total Semana.
export function desglose(c) {
  const comp = c.segs.map((s, i) => {
    const dias = s.fila.dias;
    const [ini, fin] = weekBounds(c.year, c.week);
    const a = new Date(Math.max(ini.getTime(), Date.UTC(s.ym.year, s.ym.month - 1, 1)));
    const b = new Date(Math.min(fin.getTime(), Date.UTC(s.ym.year, s.ym.month, 0)));
    const mes = MESES[s.ym.month - 1];
    return columna([s], { year: c.year, week: `${c.week}${"AB"[i]}`, titulo: `Complementaria ${"AB"[i]}`, periodo: `${a.getUTCDate()} al ${b.getUTCDate()} de ${mes}`,
      nombre: `${MESES[s.ym.month - 1][0].toUpperCase()}${MESES[s.ym.month - 1].slice(1)} ${s.ym.year}`, sub: `${a.getUTCDate()} al ${b.getUTCDate()} \u00b7 ${dias} d\u00edas`, real: true });
  });
  const [ini, fin] = weekBounds(c.year, c.week);
  const largo = (d) => `${d.getUTCDate()} de ${MESES[d.getUTCMonth()]}`;
  const periodo = ini.getUTCMonth() === fin.getUTCMonth() ? `${ini.getUTCDate()} al ${largo(fin)}` : `${largo(ini)} al ${largo(fin)}`;
  const total = columna(c.segs, { year: c.year, week: `${c.week}T`, periodo, encabezado: `${ini.getUTCDate()} ${MESES[ini.getUTCMonth()]} – ${fin.getUTCDate()} ${MESES[fin.getUTCMonth()]} ${fin.getUTCFullYear()}`, titulo: c.segs.length > 1 ? "Total semana" : "Semana completa", nombre: `Semana ${c.week}`, sub: c.sub, real: true });
  return c.segs.length > 1 ? [...comp, total] : [total];
}
